/**
 * AiBudgetAnalyticsService
 *
 * AI analysis over the organisation's budget book.
 *
 * Budget data reaches the AI service ONLY through BudgetManagement's insight-feed endpoint,
 * and every figure in the response was computed there — deterministically, from the ledger —
 * before the model ever saw it. The model narrates and prioritises; it does not calculate.
 *
 * Route: api/ai/budget-analytics (AIManagement, port 5030)
 */
import { BaseService } from '../base/BaseService';
export class AiBudgetAnalyticsService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? AiBudgetAnalyticsService.DEFAULT_SERVICE_PATH,
            basePath: AiBudgetAnalyticsService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * The org-wide report: risk flags, overspend predictions, savings, reallocation ideas,
     * anomaly explanations, spend drivers, recommendations.
     *
     * Two-tier cached on the server by the feed's snapshot hash — if the numbers haven't
     * moved, the previous report is reused rather than re-billing the model.
     */
    async generateInsightsRaw(request) {
        return this.http.post(this.buildUrl('insights'), request);
    }
    async generateInsights(request = {}) {
        const response = await this.generateInsightsRaw(request);
        if (!response.data)
            throw new Error('No data returned from budget insights');
        return response.data;
    }
    /** Free-form Q&A over the budget book. Markdown answer + the budgets it cited. */
    async queryRaw(request) {
        return this.http.post(this.buildUrl('query'), request);
    }
    async query(request) {
        const response = await this.queryRaw(request);
        if (!response.data)
            throw new Error('No data returned from the budget query');
        return response.data;
    }
    /**
     * The Report Builder's AI: explain each chart or table the user chose to include.
     * The section data is sent inline — the browser already holds it, so this adds no coupling.
     */
    async explainSectionsRaw(request) {
        return this.http.post(this.buildUrl('explain-sections'), request);
    }
    async explainSections(request) {
        const response = await this.explainSectionsRaw(request);
        if (!response.data)
            throw new Error('No data returned from explain-sections');
        return response.data;
    }
}
AiBudgetAnalyticsService.DEFAULT_SERVICE_PATH = 'ai-management';
AiBudgetAnalyticsService.BASE_PATH = 'api/ai/budget-analytics';
//# sourceMappingURL=AiBudgetAnalyticsService.js.map